import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Button } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { formatMoney } from "../../utils/constant";
import './style.scss';

type LineChartType = {
  fundId: string;
  dataChart: any[];
  onSelectPeriod: (period: any) => void;
  onSelectFromDate: (from: any) => void;
  onSelectToDate: (to: any) => void;
};

export const LineChartCPN = ({
  fundId,
  dataChart,
  onSelectPeriod,
  onSelectFromDate,
  onSelectToDate,
}: LineChartType) => {
  const [selectedPeriod, setSelectedPeriod] = useState(-1);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [showNote, setShowNote] = useState(false);

  const periods = [
    { label: "Từ đầu năm", value: -1 },
    { label: "1 năm", value: 1 },
    { label: "3 năm", value: 3 },
    { label: "5 năm", value: 5 },
    { label: "Từ ngày thành lập", value: 0 },
  ];
  useEffect(() => {
    const fundIds = ['VNAGR', 'VNGRW', 'VNBAL'];
    if (fundIds.includes(fundId) && (selectedPeriod === 0 || (fromDate && fromDate < dayjs('01/01/2016')))) {
      setShowNote(true);
    } else {
      setShowNote(false);
    }
  }, [selectedPeriod, fromDate, toDate, dataChart, fundId]);  

  return (
    <Box sx={{ p: 0 }}>
      <Card
        sx={{
          border: "none",
          boxShadow: "none",
          m: 0,
          p: 0,
          backgroundColor: "transparent",
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
            backgroundColor: "transparent",
          }}
        >
          {/* Header buttons and date pickers */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: '24px',
              pt: '24px',
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Period Buttons */}
            <Box sx={{ display: "flex", gap: '10px', flexWrap: "wrap" }}>
              {periods.map((period) => (
                <Button
                  key={period.value}
                  onClick={() => {
                    setSelectedPeriod(period.value);
                    onSelectPeriod(period.value);
                    setFromDate(null);
                    setToDate(null);
                    onSelectFromDate(null);
                    onSelectToDate(null);
                  }}
                  disableRipple
                  variant={
                    selectedPeriod === period.value ? "contained" : "outlined"
                  }
                  size="small"
                  sx={{
                    backgroundColor:
                      selectedPeriod === period.value
                        ? "#06874e"
                        : "white",
                    color: selectedPeriod === period.value ? "#fff" : "#666",
                    borderColor:
                      selectedPeriod === period.value ? "#06874e" : "#ededed",
                    borderRadius: "20px",
                    borderWidth: '2px',
                    px: 2,
                    py: 0.5,
                    fontSize: "14px",
                    textTransform: "none",
                    fontFamily: '"Manulife JH Sans", sans-serif',
                    "&:hover": {
                      boxShadow: "none",
                      borderColor: "#06874e",
                    },
                    boxShadow: "none",
                    height: '40px'
                  }}
                >
                  {period.label}
                </Button>
              ))}
            </Box>

            {/* Date Range Pickers */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "nowrap",
                maxWidth: '100%'
                // overflowX: "auto",
              }}
            >
              <DatePicker
                value={fromDate}
                onChange={(newValue) => {
                  console.log(newValue);
                  onSelectFromDate(newValue);
                  setFromDate(newValue);
                  setSelectedPeriod(-2);
                  onSelectPeriod(-2);
                }}
                format="DD/MM/YYYY"
                label="Từ ngày"
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderColor: "#ddd",
                        color: "#666",
                        fontSize: "12px",
                        minWidth: "100px",
                        height: "32px",
                      },
                    },
                  },
                }}
                maxDate={toDate ? dayjs(toDate) : dayjs()}
              />
              <span style={{ color: "#666" }}>-</span>
              <DatePicker
                value={toDate}
                onChange={(newValue) => {
                  onSelectToDate(newValue);
                  setToDate(newValue);
                  setSelectedPeriod(-2);
                  onSelectPeriod(-2);
                }}
                format="DD/MM/YYYY"
                label="Đến ngày"
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderColor: "#ddd",
                        color: "#666",
                        fontSize: "12px",
                        minWidth: "100px",
                        height: "32px",
                      },
                    },
                  },
                }}
                minDate={fromDate ? fromDate : undefined}
                maxDate={dayjs()}
              />
            </Box>
          </Box>

          {/* Chart */}
          <Box
            sx={{
              height: 400,
              width: "100%",
              backgroundColor: "#fafafa",
              borderRadius: 1,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={dataChart}
                margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#999" }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#999" }}
                  domain={["dataMin - 1", "dataMax + 1"]}
                  tickFormatter={(value) => `${value}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    fontSize: "12px",
                  }}
                  formatter={(value: any) => [`${formatMoney(value)} VND`]}
                  // labelFormatter={(label: any) => `Ngày: ${label}`}
                />
                {showNote && (
                  <Line
                    className="className"
                    type="monotone"
                    dataKey="sellPrice"
                    stroke="#d32f2f"
                    name="Giá mua đơn vị quỹ"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: "#d32f2f" }}
                  />
                )}
                <Line
                  className="className"
                  type="monotone"
                  dataKey="buyPrice"
                  name={showNote ? "Giá bán đơn vị quỹ" : "Giá đơn vị quỹ"}
                  stroke="#00A651"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: "#00A651" }}
                />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
      <Box className="note">
        {showNote ? (
          <>
            {/* <p>Trước 01/01/2016: Giá mua đơn vị Quỹ = Giá trị tài sản ròng trên mỗi đơn vị Quỹ</p>
            <p>Từ 1/1/2016: Giá mua đơn vị Quỹ = Giá bán đơn vị Quỹ = Giá trị tài sản ròng trên mỗi đơn vị Quỹ</p> */}
          </>
        ) : (
          <>
            <p style={{ lineHeight: "1.3rem" }}>Giá đơn vị Quỹ được xác định bằng giá trị tài sản ròng (NAV) trên mỗi đơn vị, và đồng thời là giá mua và giá bán của đơn vị Quỹ.</p>
          </>
        )}
      </Box>
    </Box>
  );
};
